import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

type SortDirection = "asc" | "desc";
type SortConfig<T> = {
  [key: string]: (row: T) => string | number;
};

type UseSortableTableOptions = {
  defaultColumn?: string;
  defaultDirection?: SortDirection;
};

export function useSortableTable<T>(
  data: T[],
  sortConfig: SortConfig<T>,
  options?: UseSortableTableOptions,
) {
  const [sortColumn, setSortColumn] = useState<string | null>(options?.defaultColumn ?? null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    options?.defaultDirection ?? "asc",
  );

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn(null);
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!sortColumn || !sortConfig[sortColumn]) return data;

    return [...data].sort((a, b) => {
      const aValue = sortConfig[sortColumn](a);
      const bValue = sortConfig[sortColumn](b);

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortColumn, sortDirection, sortConfig]);

  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/40 ml-1" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="h-3.5 w-3.5 ml-1" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5 ml-1" />
    );
  };

  return {
    sortedData,
    handleSort,
    renderSortIcon,
    sortColumn,
    sortDirection,
  };
}
